import { React, useContext, useEffect, useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import AddBusinessRoundedIcon from '@mui/icons-material/AddBusinessRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import DomainAddRoundedIcon from '@mui/icons-material/DomainAddRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import NoteAddRoundedIcon from '@mui/icons-material/NoteAddRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Tooltip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useLocation, useNavigate } from 'react-router-dom';

import { Alerta, PopupDialog } from '../../components/alerta';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import DadosUsuario from '../../utils/context';
import { distractCategories } from '../../utils/distractCategories';
import { fetchFlask } from '../../utils/fetchFlask';
import { maskCEP, maskCpfCnpj, maskPhone } from '../../utils/maskCpfCnpj';
import { formatDecimal } from '../../utils/numberUtils';
import {
    CEP_DIGITS_LENGTH,
    getDigits,
    isCnpjDocument
} from '../../utils/stringFormat';
import { isPostSaleProfile } from '../../utils/userProfile';
import '../css/additive.css';

const additiveTypeLabels = {
    adicao: 'Adição',
    exclusao: 'Exclusão',
    desconto: 'Desconto',
    substituicao: 'Substituição',
    'retorno multa': 'Retorno Multa'
};

const postSaleAdditiveTypes = [
    { value: 'exclusao', label: additiveTypeLabels.exclusao },
    { value: 'desconto', label: additiveTypeLabels.desconto },
    { value: 'substituicao', label: additiveTypeLabels.substituicao },
    { value: 'retorno multa', label: additiveTypeLabels['retorno multa'] }
];

const ccAddressFieldOrder = ['rua', 'numero', 'complemento', 'bairro', 'cidade', 'estado', 'cep'];
const requiredCcAddressFields = ccAddressFieldOrder.filter((field) => field !== 'complemento');
const emptyCcAddress = ccAddressFieldOrder.reduce((acc, field) => ({ ...acc, [field]: '' }), {});
const stateAliases = {
    PE: 'PE',
    Pernambuco: 'PE',
    RN: 'RN',
    'Rio Grande do Norte': 'RN'
};
const distributorByState = {
    PE: 'NEOENERGIA PE',
    RN: 'COSERN'
};

const isCcAddressComplete = (address) =>
    requiredCcAddressFields.every((field) => (address?.[field] || '').trim());

const formatCcAddress = (address) => {
    const complemento = address.complemento?.trim();
    return [
        `${address.rua}, ${address.numero}${complemento ? ` - ${complemento}` : ''}`,
        address.bairro,
        `${address.cidade} - ${address.estado}`,
        address.cep
    ].filter(Boolean).join(', ');
};

const GenerateAdditive = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dadosUsuario = useContext(DadosUsuario);
    const isPostSale = isPostSaleProfile(dadosUsuario?.autorizacoes);
    const leadData = location.state?.leadData;
    const clienteId = leadData?.cliente_id ?? '';

    const [client, setClient] = useState(null);
    const [representativeName, setRepresentativeName] = useState('');
    const [representativeCpf, setRepresentativeCpf] = useState('');
    const [email, setEmail] = useState(() => leadData?.email ?? '');
    const [rows, setRows] = useState([]);
    const [cc, setCC] = useState('');
    const [ccAddress, setCCAddress] = useState(emptyCcAddress);
    const [ccConsumption, setCcConsumption] = useState('');
    const [ccInstallation, setCcInstallation] = useState('');
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);
    const [additiveType, setAdditiveType] = useState(() => (isPostSale ? 'exclusao' : 'adicao'));
    const [oldDiscount, setOldDiscount] = useState('');
    const [newDiscount, setNewDiscount] = useState('');
    const [verificationType, setVerificationType] = useState('EMAIL');
    const [cpfCnpj, setCpfCnpj] = useState(() => leadData?.cpf ? maskCpfCnpj(leadData.cpf) : '');
    const [selectedCCsForExclusion, setSelectedCCsForExclusion] = useState([]);
    const [selectedCCForSubstitution, setSelectedCCForSubstitution] = useState(null);
    const [phone1, setPhone1] = useState(() => leadData?.telefone ? maskPhone(leadData.telefone) : '');
    const [motivo, setMotivo] = useState('');
    const [openReturnDialog, setOpenReturnDialog] = useState(false);
    const [isFetchingCep, setIsFetchingCep] = useState(false);

    const additiveDistractCategories = distractCategories.filter((category) => category.value !== 'Repasse Imóvel');

    const applyLeadDefaults = (lead) => {
        setEmail(lead.email ?? '');
        setCpfCnpj(lead.cpf ? maskCpfCnpj(lead.cpf) : '');
        setPhone1(lead.telefone ? maskPhone(lead.telefone) : '');
    };

    const resetOperationFields = () => {
        setRows([]);
        setSelectedCCsForExclusion([]);
        setSelectedCCForSubstitution(null);
        setMotivo('');
        setNewDiscount('');
    };

    const updateCcAddressField = (field, value) => {
        setCCAddress((prev) => ({ ...prev, [field]: value }));
    };

    const updateCcCep = (value) => {
        updateCcAddressField('cep', maskCEP(value));
    };

    async function getCcAddressData () {
        setIsFetchingCep(true);

        const body = JSON.stringify({
            cep: ccAddress.cep
        });

        try {
            const response = await fetchFlask('buscarDadosCEP', body);

            if (response.status === 'success') {
                setCCAddress((prev) => ({
                    ...prev,
                    rua: response.data.logradouro ?? '',
                    bairro: response.data.bairro ?? '',
                    cidade: response.data.localidade ?? '',
                    estado: stateAliases[response.data.estado] ?? stateAliases[response.data.uf] ?? ''
                }));
            } else {
                handleAlerta(response);
            }
        } finally {
            setIsFetchingCep(false);
        }
    }

    const normalizeClientData = (response) => {
        const candidate = response?.client || response?.dados?.client || response?.dados || response;
        const rawClient = Array.isArray(candidate) ? candidate[0] : candidate;

        if (!rawClient || typeof rawClient !== 'object' || !rawClient.cliente) {
            return null;
        }

        return {
            id: rawClient.cliente_id,
            cliente: rawClient.cliente,
            codigoCliente: rawClient.codigo_cliente,
            desconto: rawClient.desconto,
            cpfCnpj: [...new Set((rawClient.documents || []).filter(Boolean))],
            ccs: [...new Set((rawClient.ccs || []).filter(Boolean))],
            emails: [...new Set((rawClient.emails || []).filter(Boolean))],
            phones: [...new Set((rawClient.phones || []).filter(Boolean))]
        };
    };

    const columnsAdd = [
        {
            field: 'ccCol',
            headerName: 'CC',
            width: 150,
            editable: true
        },
        {
            field: 'addressCol',
            headerName: 'Endereço',
            width: 260,
            editable: false
        },
        {
            field: 'installationCol',
            headerName: 'Instalação',
            width: 130,
            editable: true
        },
        {
            field: 'consumptionCol',
            headerName: 'Consumo',
            type: 'number',
            width: 110,
            editable: true,
            renderCell: (params) => (
                formatDecimal(params.value)
            )
        },
        {
            field: 'Upload',
            headerName: 'Conta de Energia',
            width: 280,
            renderCell: (params) => (
                <FormControl sx={{ display: 'flex', marginTop: '5px' }}>
                    <TextField
                        type='file'
                        size='small'
                        required={additiveType === 'adicao'}
                        inputProps={{ accept: 'application/pdf, image/*' }}
                        onChange={(e) => handleFileChange(e.target.files[0], params.row.id)}
                    />
                </FormControl>
            )
        },
        {
            field: ' ',
            renderCell: (params) => (
                <IconButton edge="end" onClick={() => deleteCC(params.row.id)}>
                    <DeleteIcon />
                </IconButton>
            )
        }
    ];

    const columnsSubs = [...columnsAdd];
    columnsSubs.splice(0, 0, {
        field: 'oldCcCol',
        headerName: 'CC Antiga',
        width: 150,
        editable: false
    });

    const handleFileChange = (file, rowId) => {
        setRows((prevRows) =>
            prevRows.map((row) =>
                row.id === rowId ? { ...row, file } : row
            )
        );
    };

    const deleteCC = (id) => {
        setRows((prevRows) => prevRows.filter((row) => row.id !== id));
    };

    const processRowUpdate = (newRow) => {
        setRows((prevRows) =>
            prevRows.map((row) => (row.id === newRow.id ? newRow : row))
        );
        return newRow;
    };

    const addUnits = () => {
        if (!cc || !isCcAddressComplete(ccAddress) || !ccConsumption || !ccInstallation) {
            handleAlerta({
                status: 'warning',
                message: 'Preencha todos os campos para adicionar uma unidade.'
            });
            return;
        }

        if (additiveType === 'substituicao' && !selectedCCForSubstitution) {
            handleAlerta({
                status: 'warning',
                message: 'Selecione uma CC antiga para substituição.'
            });
            return;
        }

        const newRow = {
            id: rows.length + 1,
            ccCol: cc,
            addressCol: formatCcAddress(ccAddress),
            addressFields: { ...ccAddress },
            installationCol: ccInstallation,
            consumptionCol: ccConsumption
        };

        if (additiveType === 'substituicao') {
            newRow.oldCcCol = selectedCCForSubstitution;
        }

        setRows([...rows, newRow]);
        setCC('');
        setCCAddress(emptyCcAddress);
        setCcConsumption('');
        setCcInstallation('');
        if (additiveType === 'substituicao') {
            setSelectedCCForSubstitution(null);
        }
    };

    async function getClientInfo () {
        if (!clienteId) {
            return;
        }

        const body = JSON.stringify({
            cliente_id: clienteId,
            tipo: 'aditivo'
        });

        const response = await fetchFlask('getClientData', body);

        if (response.status !== 'error') {
            const normalizedClient = normalizeClientData(response);

            if (!normalizedClient) {
                handleAlerta({ status: 'error', message: 'Cliente não encontrado.' });
                return;
            }

            setClient(normalizedClient);
            setOldDiscount(normalizedClient.desconto || '');
            applyLeadDefaults(leadData);
        } else {
            handleAlerta(response);
        }
    }

    useEffect(() => {
        getClientInfo();
    }, []);

    async function submitAdicionarAditivo(e) {
        e.preventDefault();
        setIsSubmit(true);

        if (phone1.length < 14) {
            setMessage('Telefone no formato inválido!');
            setStatus('error');
            setOpenAlerta(true);
            setIsSubmit(false);
            return;
        };

        const formData = new FormData();
        formData.append('additiveType', additiveType);
        formData.append('verificationType', verificationType);
        formData.append('clienteId', parseInt(client.id));
        formData.append('clientName', client.cliente);
        formData.append('clientCode', client.codigoCliente);
        formData.append('cpfCnpj', cpfCnpj);
        formData.append('email', email);
        formData.append('phone1', phone1);

        if (isCnpjDocument(cpfCnpj)) {
            formData.append('representativeName', representativeName);
            formData.append('representativeCpf', representativeCpf);
        }
        if (verificationType === 'SMS') {
            formData.append('phone1', phone1);
        }

        if (additiveType === 'exclusao') {
            formData.append('ccsToExclude', JSON.stringify(selectedCCsForExclusion));
            formData.append('motivo', motivo);
        } else if (additiveType === 'adicao' || additiveType === 'substituicao') {
            if (rows.length === 0) {
                setMessage('Adicione no mínimo uma unidade!');
                setStatus('error');
                setOpenAlerta(true);
                setIsSubmit(false);
                return;
            }
            let units = [];
            units = rows.map(row => {
                const unitData = {
                    cc: row.ccCol,
                    addressFields: row.addressFields,
                    installation: row.installationCol,
                    consumption: row.consumptionCol,
                    ...(additiveType === 'substituicao' && { oldCc: row.oldCcCol })
                };

                if (row.file) {
                    formData.append(row.ccCol, row.file);
                }

                return unitData;
            });
            formData.append('units', JSON.stringify(units));
            if (additiveType === 'substituicao') {
                const ccsToExclude = rows.map(row => row.oldCcCol);

                formData.append('ccsToExclude', JSON.stringify(ccsToExclude));
            }
        } else if (additiveType === 'desconto') {
            formData.append('oldDiscount', String(oldDiscount));
            formData.append('newDiscount', String(newDiscount));
        }

        if (leadData?.lead_id) {
            formData.append('lead_id', leadData?.lead_id);
        }

        const response = await fetchFlask('generateAdditive', formData);

        if (response.status === 'success') {
            resetOperationFields();
            setCC('');
            setCCAddress(emptyCcAddress);
            setCcConsumption('');
            setCcInstallation('');
        }
        handleAlerta(response);
        setIsSubmit(false);
    }

    function handleAlerta(response) {
        setMessage(response.message);
        setStatus(response.status);
        setOpenAlerta(true);
    }

    const navigateBackToLead = () => {
        if (!leadData?.lead_id) {
            navigate('/CRM/leads');
            return;
        }

        navigate(`/CRM/leads/${leadData.lead_id}`, {
            state: {
                leadData,
                leadUnits: location.state?.leadUnits
            }
        });
    };

    const selectedTypeLabel = additiveTypeLabels[additiveType] ?? additiveType;
    const clientLabel = client ? `${client.cliente} - ${client.id}` : 'Cliente não carregado';
    const documentLabel = cpfCnpj || 'Não informado';
    const contactLabel = phone1 || email || 'Não informado';
    const unitsSummaryLabel = additiveType === 'exclusao'
        ? `${selectedCCsForExclusion.length} selecionada${selectedCCsForExclusion.length === 1 ? '' : 's'}`
        : additiveType === 'retorno multa'
            ? 'Não se aplica'
            : `${rows.length} adicionada${rows.length === 1 ? '' : 's'}`;
    const rowDistributors = [...new Set(rows.map((row) => distributorByState[row.addressFields?.estado]).filter(Boolean))];
    const operationDetailLabel = additiveType === 'desconto'
        ? (newDiscount ? `${oldDiscount || 0}% para ${newDiscount}%` : 'Novo desconto pendente')
        : additiveType === 'exclusao'
            ? (motivo || 'Motivo pendente')
            : additiveType === 'retorno multa'
                ? 'Retorno de multa'
                : rowDistributors.join(', ');
    const completedFields = [
        client,
        cpfCnpj,
        phone1.length >= 14,
        email,
        verificationType,
        additiveType === 'desconto'
            ? newDiscount
            : additiveType === 'exclusao'
                ? selectedCCsForExclusion.length > 0 && motivo
                : additiveType === 'retorno multa'
                    ? true
                    : rows.length > 0
    ].filter(Boolean).length;
    const shouldDisableSubmit = ((additiveType === 'adicao' || additiveType === 'substituicao') && rows.length === 0) ||
        (additiveType === 'exclusao' && (selectedCCsForExclusion.length === 0 || !motivo));
    const hasPendingUnitFields = !cc || !isCcAddressComplete(ccAddress) || !ccConsumption || !ccInstallation ||
        (additiveType === 'substituicao' && !selectedCCForSubstitution);
    const shouldDisableAddUnit = !client || hasPendingUnitFields;
    const addUnitTooltip = !client
        ? 'Carregue o cliente antes de adicionar unidade.'
        : additiveType === 'substituicao' && !selectedCCForSubstitution
            ? 'Selecione a CC antiga e preencha CC nova, consumo, instalação e endereço.'
            : 'Preencha conta contrato, consumo, instalação e endereço.';
    const clientFieldScope = `${client?.id || 'no-client'}-${additiveType}`;
    const hasAddressInput = Object.values(ccAddress).some((value) => (value || '').trim());
    const hasReturnRisk = rows.length > 0 || cc || hasAddressInput || ccConsumption || ccInstallation ||
        selectedCCForSubstitution || selectedCCsForExclusion.length > 0 ||
        motivo || newDiscount || representativeName || representativeCpf;
    const handleReturnToLead = () => {
        if (hasReturnRisk) {
            setOpenReturnDialog(true);
            return;
        }

        navigateBackToLead();
    };

    return (
        <Box className='mainBox ui-workflow-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PopupDialog
                titulo='Voltar ao lead'
                texto='Tem certeza que deseja voltar para a página do lead? As informações preenchidas neste aditivo serão descartadas.'
                open={openReturnDialog}
                handleClose={() => setOpenReturnDialog(false)}
                funcao={navigateBackToLead}
            />
            <PageHeader>
                Aditivo
            </PageHeader>

            <Box className='additive-topbar'>
                <Button
                    type='button'
                    variant='outlined'
                    className='additive-return-button'
                    startIcon={<ArrowBackRoundedIcon />}
                    onClick={handleReturnToLead}
                >
                    Voltar ao lead
                </Button>
            </Box>

            <Box className='additive-mode-panel'>
                <Box className='additive-mode-copy'>
                    {additiveType === 'substituicao'
                        ? <SwapHorizRoundedIcon />
                        : <NoteAddRoundedIcon />}
                    <Box>
                        <span>{isPostSale ? 'Tipo de aditivo' : 'Origem CRM'}</span>
                        <strong>{isPostSale ? selectedTypeLabel : 'Adição de unidade'}</strong>
                    </Box>
                </Box>

                {isPostSale
                    ? (
                    <FormControl className='additive-type-control' required>
                        <InputLabel id='labelAdditiveType'>Tipo de Aditivo</InputLabel>
                        <Select
                            id='selectAdditiveType'
                            labelId='labelAdditiveType'
                            value={additiveType}
                            onChange={(e) => {
                                setAdditiveType(e.target.value);
                                resetOperationFields();
                            }}
                            label='Tipo de Aditivo'
                            required
                        >
                            {postSaleAdditiveTypes.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    )
                    : (
                    <Box className='additive-mode-note'>
                        <span>Tipo herdado do lead</span>
                        <strong>Adição</strong>
                    </Box>
                    )}
            </Box>

            <form
                id='formAditivo'
                onSubmit={submitAdicionarAditivo}
                className='ui-workflow-layout'
                autoComplete='off'
            >
                <Box className='ui-workflow-workspace'>
                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <ManageAccountsRoundedIcon />
                            <Box>
                                <span>Etapa 1</span>
                                <strong>Cliente e verificação</strong>
                            </Box>
                        </Box>

                        <Box className='additive-client-strip'>
                            <BadgeRoundedIcon />
                            <Box>
                                <span>{isPostSale ? 'Matrícula herdada do lead' : 'Matrícula e tipo herdados do CRM'}</span>
                                <strong>{clienteId || 'Não informada'} | {selectedTypeLabel}</strong>
                            </Box>
                        </Box>

                        <Box className='additive-client-grid' key={`client-fields-${clientFieldScope}`}>
                            <TextField
                                id='additive-client-name'
                                name='additive-client-name'
                                label='Cliente'
                                value={client ? `${client.cliente} - ${client.id}` : ''}
                                required
                                fullWidth
                                autoComplete='off'
                                InputProps={{ readOnly: true }}
                            />

                            <Autocomplete
                                id='additive-client-document'
                                key={`${clientFieldScope}-document`}
                                freeSolo
                                disabled={!client}
                                disablePortal
                                options={Array.isArray(client?.cpfCnpj) ? client.cpfCnpj : []}
                                getOptionLabel={(option) => String(option)}
                                isOptionEqualToValue={(option, value) =>
                                    getDigits(option) === getDigits(value)
                                }
                                inputValue={cpfCnpj}
                                onInputChange={(event, newInputValue, reason) => {
                                    if (reason === 'input') {
                                        setCpfCnpj(maskCpfCnpj(newInputValue));
                                    }
                                }}
                                value={cpfCnpj || null}
                                onChange={(event, newValue) => {
                                    if (newValue) {
                                        setCpfCnpj(maskCpfCnpj(typeof newValue === 'string' ? newValue : String(newValue)));
                                    } else {
                                        setCpfCnpj('');
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        id='additive-client-document-input'
                                        required
                                        label="CPF/CNPJ"
                                        fullWidth
                                        inputProps={{
                                            ...params.inputProps,
                                            autoComplete: 'new-password',
                                            maxLength: 18,
                                            name: 'additive-client-document'
                                        }}
                                    />
                                )}
                            />

                            {isCnpjDocument(cpfCnpj) && (
                                <>
                                    <TextField
                                        id='additive-representative-name'
                                        name='additive-representative-name'
                                        required
                                        label='Nome do Representante'
                                        value={representativeName}
                                        onChange={e => setRepresentativeName(e.target.value)}
                                        fullWidth
                                        autoComplete='off'
                                    />
                                    <TextField
                                        id='additive-representative-document'
                                        name='additive-representative-document'
                                        required
                                        label='CPF do Representante'
                                        value={representativeCpf}
                                        inputProps={{ autoComplete: 'new-password', maxLength: 14 }}
                                        onChange={e => setRepresentativeCpf(maskCpfCnpj(e.target.value))}
                                        fullWidth
                                    />
                                </>
                            )}

                            <Autocomplete
                                id='additive-client-phone'
                                key={`${clientFieldScope}-phone`}
                                freeSolo
                                disabled={!client}
                                disablePortal
                                options={Array.isArray(client?.phones) ? client.phones : []}
                                getOptionLabel={(option) => String(option)}
                                isOptionEqualToValue={(option, value) =>
                                    getDigits(option) === getDigits(value)
                                }
                                inputValue={phone1}
                                onInputChange={(event, newInputValue, reason) => {
                                    if (reason === 'input') {
                                        setPhone1(maskPhone(newInputValue));
                                    }
                                }}
                                value={phone1 || null}
                                onChange={(event, newValue) => {
                                    if (newValue) {
                                        setPhone1(maskPhone(typeof newValue === 'string' ? newValue : String(newValue)));
                                    } else {
                                        setPhone1('');
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        id='additive-client-phone-input'
                                        required
                                        label="Telefone"
                                        fullWidth
                                        inputProps={{
                                            ...params.inputProps,
                                            autoComplete: 'new-password',
                                            maxLength: 15,
                                            name: 'additive-client-phone'
                                        }}
                                    />
                                )}
                            />

                            <Autocomplete
                                id='additive-client-email'
                                freeSolo
                                disabled={!client}
                                disablePortal
                                key={`${clientFieldScope}-email`}
                                options={Array.isArray(client?.emails) ? client.emails : []}
                                getOptionLabel={(option) => String(option)}
                                inputValue={email}
                                value={email || null}
                                onChange={(event, newValue) => setEmail(newValue || '')}
                                onInputChange={(event, newInputValue, reason) => {
                                    if (reason === 'input') {
                                        setEmail(newInputValue);
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        id='additive-client-email-input'
                                        required
                                        label="Email"
                                        InputProps={{
                                            ...params.InputProps
                                        }}
                                        inputProps={{
                                            ...params.inputProps,
                                            autoComplete: 'new-password',
                                            name: 'additive-client-email'
                                        }}
                                    />
                                )}
                            />

                            <FormControl className='ui-control'>
                                <InputLabel id='labelVerificationType'>Verificação</InputLabel>
                                <Select
                                    id='selectVerificationType'
                                    name='additive-verification-type'
                                    disabled={!client}
                                    labelId='labelVerificationType'
                                    label='Verificação'
                                    value={verificationType}
                                    required
                                    onChange={(e) => setVerificationType(e.target.value)}
                                >
                                    <MenuItem value={'SMS'}>SMS</MenuItem>
                                    <MenuItem value={'EMAIL'}>Email</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    {(additiveType === 'adicao' || additiveType === 'substituicao') && (
                        <Box className='ui-workflow-panel'>
                            <Box className='ui-heading'>
                                {additiveType === 'substituicao' ? <SwapHorizRoundedIcon /> : <DomainAddRoundedIcon />}
                                <Box>
                                    <span>Etapa 2</span>
                                    <strong>{additiveType === 'substituicao' ? 'Substituição de unidade' : 'Unidades adicionadas'}</strong>
                                </Box>
                            </Box>

                            <Box className='additive-units-box'>
                                <DataGrid
                                    rows={rows}
                                    columns={additiveType === 'substituicao' ? columnsSubs : columnsAdd}
                                    processRowUpdate={processRowUpdate}
                                    localeText={{ noRowsLabel: 'Sem Unidades' }}
                                    hideFooter
                                    autoHeight
                                />
                            </Box>

                            <Box className={`additive-unit-form ${additiveType === 'substituicao' ? 'is-substitution' : ''}`}>
                                {additiveType === 'substituicao' && (
                                    <Autocomplete
                                        id="ccs-autocomplete"
                                        disabled={!client}
                                        options={(client?.ccs || []).filter(cc =>
                                            !rows.some(row => row.oldCcCol === cc)
                                        )}
                                        value={selectedCCForSubstitution}
                                        getOptionLabel={(option) => option?.toString() || ''}
                                        isOptionEqualToValue={(option, value) =>
                                            option?.toString() === value?.toString()
                                        }
                                        onChange={(event, newValue) => setSelectedCCForSubstitution(newValue)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="CC Antiga"
                                                placeholder="CC Antiga"
                                                fullWidth
                                            />
                                        )}
                                    />
                                )}

                                <TextField
                                    label={additiveType === 'substituicao' ? 'CC Nova' : 'Conta Contrato'}
                                    value={cc}
                                    onChange={(e) => setCC(e.target.value.trim())}
                                />
                                <TextField
                                    label={additiveType === 'substituicao' ? 'Instalação Nova' : 'Instalação'}
                                    value={ccInstallation}
                                    onChange={(e) => setCcInstallation(e.target.value.trim())}
                                />
                                <TextField
                                    type='number'
                                    label='Consumo'
                                    value={ccConsumption}
                                    onChange={(e) => setCcConsumption(e.target.value.trim())}
                                />

                                <Box className='additive-unit-address'>
                                    <span className='additive-unit-address-heading'>Endereço da unidade</span>
                                    <TextField
                                        label='Rua'
                                        value={ccAddress.rua}
                                        onChange={(e) => updateCcAddressField('rua', e.target.value)}
                                        fullWidth
                                    />
                                    <TextField
                                        label='Número'
                                        value={ccAddress.numero}
                                        onChange={(e) => updateCcAddressField('numero', e.target.value)}
                                        fullWidth
                                    />
                                    <TextField
                                        label='Complemento'
                                        value={ccAddress.complemento}
                                        onChange={(e) => updateCcAddressField('complemento', e.target.value)}
                                        fullWidth
                                    />
                                    <TextField
                                        label='Bairro'
                                        value={ccAddress.bairro}
                                        onChange={(e) => updateCcAddressField('bairro', e.target.value)}
                                        fullWidth
                                    />
                                    <TextField
                                        label='Cidade'
                                        value={ccAddress.cidade}
                                        onChange={(e) => updateCcAddressField('cidade', e.target.value)}
                                        fullWidth
                                    />
                                    <FormControl fullWidth>
                                        <InputLabel id='labelCcAddressEstado'>Estado</InputLabel>
                                        <Select
                                            labelId='labelCcAddressEstado'
                                            label='Estado'
                                            value={ccAddress.estado}
                                            onChange={(e) => updateCcAddressField('estado', e.target.value)}
                                        >
                                            <MenuItem value='PE'>PE</MenuItem>
                                            <MenuItem value='RN'>RN</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        label='CEP'
                                        value={ccAddress.cep}
                                        inputProps={{ maxLength: 9 }}
                                        onChange={(e) => updateCcCep(e.target.value)}
                                        fullWidth
                                    />
                                    <Button
                                        variant='contained'
                                        onClick={getCcAddressData}
                                        disabled={isFetchingCep || getDigits(ccAddress.cep).length !== CEP_DIGITS_LENGTH}
                                        startIcon={<SearchRoundedIcon />}
                                    >
                                        {isFetchingCep ? 'Buscando...' : 'Buscar CEP'}
                                    </Button>
                                </Box>
                            </Box>

                            <Box className='additive-unit-action'>
                                <Tooltip title={shouldDisableAddUnit ? addUnitTooltip : ''}>
                                    <span>
                                        <Button
                                            variant='contained'
                                            onClick={addUnits}
                                            disabled={shouldDisableAddUnit}
                                            startIcon={<AddBusinessRoundedIcon />}
                                        >
                                            Adicionar Unidade
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>
                        </Box>
                    )}

                    {additiveType === 'exclusao' && (
                        <Box className='ui-workflow-panel'>
                            <Box className='ui-heading'>
                                <TuneRoundedIcon />
                                <Box>
                                    <span>Etapa 2</span>
                                    <strong>Unidades que sairão do contrato</strong>
                                </Box>
                            </Box>

                            <Box className='additive-removal-grid'>
                                <Autocomplete
                                    multiple
                                    id="ccs-autocomplete"
                                    fullWidth
                                    disabled={!client}
                                    options={client?.ccs || []}
                                    value={selectedCCsForExclusion}
                                    disableCloseOnSelect
                                    onChange={(event, newValue) => setSelectedCCsForExclusion(newValue)}
                                    renderOption={(props, option, { selected }) => (
                                        <li {...props}>
                                            <Checkbox
                                                style={{ marginRight: 8 }}
                                                checked={selected}
                                            />
                                            {option}
                                        </li>
                                    )}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip
                                                key={option}
                                                variant="outlined"
                                                label={option}
                                                {...getTagProps({ index })}
                                            />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="CCs Antigas"
                                            placeholder="CCs Antigas"
                                            fullWidth
                                        />
                                    )}
                                />

                                <FormControl fullWidth>
                                    <InputLabel id='labelCat'>Motivo</InputLabel>
                                    <Select
                                        id='motivo'
                                        labelId='labelCat'
                                        label='motivo'
                                        value={motivo}
                                        onChange={(e) => setMotivo(e.target.value)}
                                        required
                                    >
                                        {additiveDistractCategories.map((category) => (
                                            <MenuItem key={category.value} value={category.value}>
                                                {category.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>
                    )}

                    {additiveType === 'desconto' && (
                        <Box className='ui-workflow-panel'>
                            <Box className='ui-heading'>
                                <TuneRoundedIcon />
                                <Box>
                                    <span>Etapa 2</span>
                                    <strong>Alteração de desconto</strong>
                                </Box>
                            </Box>

                            <Box className='additive-discount-grid'>
                                <TextField
                                    label='Desconto atual'
                                    value={oldDiscount ? `${oldDiscount}%` : ''}
                                    InputProps={{ readOnly: true }}
                                />
                                <TextField
                                    label="Desconto novo"
                                    value={newDiscount}
                                    onChange={(e) => {
                                        const numeric = e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.');
                                        setNewDiscount(numeric);
                                    }}
                                    inputProps={{ maxLength: 2 }}
                                    InputProps={{
                                        endAdornment: <span style={{ marginLeft: 4 }}>%</span>
                                    }}
                                />
                            </Box>
                        </Box>
                    )}
                </Box>

                <Box className='ui-workflow-summary'>
                    <Box className='ui-heading ui-heading-divided'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>Revisão do aditivo</strong>
                        </Box>
                    </Box>

                    <Box className='ui-workflow-summary-list'>
                        <Box>
                            <span>Origem</span>
                            <strong>{isPostSale ? 'Relacionamento' : 'CRM'}</strong>
                        </Box>
                        <Box>
                            <span>Tipo</span>
                            <strong>{selectedTypeLabel}</strong>
                        </Box>
                        <Box>
                            <span>Cliente</span>
                            <strong className={!client ? 'is-empty' : undefined}>{clientLabel}</strong>
                        </Box>
                        <Box>
                            <span>CPF/CNPJ</span>
                            <strong className={!cpfCnpj ? 'is-empty' : undefined}>{documentLabel}</strong>
                        </Box>
                        <Box>
                            <span>Contato</span>
                            <strong className={!phone1 && !email ? 'is-empty' : undefined}>{contactLabel}</strong>
                        </Box>
                        <Box>
                            <span>{additiveType === 'desconto' ? 'Desconto' : 'Unidades'}</span>
                            <strong>{additiveType === 'desconto' ? operationDetailLabel : unitsSummaryLabel}</strong>
                        </Box>
                        <Box>
                            <span>{additiveType === 'exclusao' ? 'Motivo' : 'Operação'}</span>
                            <strong className={!operationDetailLabel ? 'is-empty' : undefined}>{operationDetailLabel}</strong>
                        </Box>
                    </Box>

                    <Box className='ui-attention'>
                        <WarningAmberRoundedIcon />
                        <p>
                            O aditivo será gerado com os dados de verificação, cliente e unidades revisados nesta tela.
                        </p>
                    </Box>

                    <Box className='ui-attention is-ok ui-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos críticos</span>
                            <strong>{completedFields}/6 preenchidos</strong>
                        </Box>
                    </Box>

                    <SubmitButton isSubmit={isSubmit} disabled={shouldDisableSubmit} startIcon={<NoteAddRoundedIcon />}>
                        Gerar Aditivo
                    </SubmitButton>
                </Box>
            </form>
        </Box>
    );
};

export default GenerateAdditive;
